import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';

const PLAYLIST_MODES = [
  { value: 'sequential', label: 'Sequential' },
  { value: 'random', label: 'Random' },
  { value: 'rotation', label: 'Rotation' },
];

const MODE_ICONS = {
  sequential: '🔢',
  random: '🎲',
  rotation: '🔄',
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'Todos los medios' },
  { value: 'music', label: 'Música' },
  { value: 'program', label: 'Programa' },
  { value: 'advertising', label: 'Publicidad' },
  { value: 'jingle', label: 'Jingle' },
  { value: 'promo', label: 'Promo' },
  { value: 'playlist', label: 'Playlist' },
  { value: 'special', label: 'Especial' },
  { value: 'fallback', label: 'Fallback' },
];

export default function PlaylistEditor({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = editMode && Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    mode: 'sequential',
    is_active: true,
    is_public: false,
    category: '',
  });

  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchPlaylist();
    }
  }, [id]);

  const fetchPlaylist = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/playlists/${id}`);
      setFormData({
        name: data.name || '',
        description: data.description || '',
        mode: data.mode || 'sequential',
        is_active: data.is_active ?? true,
        is_public: data.is_public ?? false,
        category: data.category || '',
      });
    } catch (err) {
      console.error('[PlaylistEditor] Fetch failed:', err);
    }
    try {
      const { data } = await api.get(`/api/playlists/${id}/items`);
      setItems(data);
    } catch (err) {
      console.error('[PlaylistEditor] Failed to fetch items:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchMedia = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const params = { search: term, limit: 20, processing_status: 'ready' };
      if (formData.category) params.category = formData.category;
      const { data } = await api.get('/api/media', { params });
      const files = data.files || [];
      setSearchResults(files.map((track) => ({
        id: track.id,
        media_file_id: track.id,
        title: track.metadata?.title || track.original_filename,
        artist: track.metadata?.artist || '',
        file_path: track.stored_filename,
        duration_seconds: track.duration_seconds,
      })));
    } catch (err) {
      console.error('[PlaylistEditor] Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [formData.category]);

  useEffect(() => {
    const timer = setTimeout(() => searchMedia(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchMedia]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const addItem = async (media) => {
    setSearchLoading(true);
    try {
      const newItem = {
        id: uuidv4(),
        temp_id: uuidv4(),
        media_id: media.id,
        title: media.title,
        artist: media.artist,
        duration_seconds: media.duration_seconds,
        sort_order: items.length,
        _new: true,
      };
      setItems([...items, newItem]);
      setSearchResults([]);
      setSearchTerm('');
    } catch (err) {
      console.error('[PlaylistEditor] Add failed:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const removeItem = (itemId) => {
    setItems(items.filter((i) => i.id !== itemId && i.temp_id !== itemId));
  };

  const handleDragStart = (item) => {
    setDraggedItem(item);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) return;

    const oldIndex = items.findIndex((i) => i.id === draggedItem.id || i.temp_id === draggedItem.id);
    const newIndex = items.findIndex((i) => i.id === targetItem.id || i.temp_id === targetItem.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = [...items];
    const [removed] = newItems.splice(oldIndex, 1);
    newItems.splice(newIndex, 0, removed);

    const reordered = newItems.map((item, idx) => ({ ...item, sort_order: idx }));
    setItems(reordered);
    setDraggedItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setErrors({ name: 'Playlist name is required' });
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/playlists/${id}`, formData);
      } else {
        const resp = await api.post('/api/playlists', formData);
        const newId = resp.data.id;
        await api.put(`/api/playlists/${newId}/items`, items.filter((i) => !i._new).map((i, idx) => ({
          media_id: i.media_id,
          sort_order: idx,
        })));
        navigate(`/playlists/${newId}`);
        return;
      }

      // Update items that are new
      const newItems = items.filter((i) => i._new);
      if (newItems.length > 0 && isEdit) {
        for (const item of newItems) {
          try {
            await api.post(`/api/playlists/${id}/items`, {
              media_id: item.media_id,
              sort_order: item.sort_order,
            });
          } catch (err) {
            console.error('[PlaylistEditor] Add item failed:', err);
          }
        }
      }

      // Reorder existing items
      const existingItems = items.filter((i) => !i._new);
      if (existingItems.length > 0 && isEdit) {
        await api.put(`/api/playlists/${id}/items/reorder`, {
          items: existingItems.map((i) => ({ id: i.id, sort_order: i.sort_order })),
        });
      }

      navigate('/playlists');
    } catch (err) {
      console.error('[PlaylistEditor] Save failed:', err);
      setErrors({ submit: 'Failed to save playlist.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading playlist...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {isEdit ? 'Edit Playlist' : 'Create Playlist'}
        </h1>
        <Link to="/playlists" className="btn btn-secondary btn-sm">
          Back to Playlists
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Playlist settings */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">Settings</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-dark-300 mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input ${errors.name ? 'border-error-500' : ''}`}
                placeholder="Playlist name"
              />
              {errors.name && <p className="text-xs text-error-400 mt-1">{errors.name}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-dark-300 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="input min-h-[60px]"
                placeholder="Optional description..."
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-dark-300 mb-1">Mode</label>
              <select name="mode" value={formData.mode} onChange={handleChange} className="input">
                {PLAYLIST_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-dark-300 mb-1">Category Filter</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                className="input"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
                />
                <label htmlFor="is_active" className="text-sm text-dark-300">Active</label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_public"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
                />
                <label htmlFor="is_public" className="text-sm text-dark-300">Public (visible on player)</label>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">Add Tracks</h3>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search media to add..."
                className="input pr-10"
                disabled={!isEdit}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-500">🔍</span>
            </div>
            {!isEdit && (
              <p className="text-xs text-dark-500 mt-1">Save to add tracks</p>
            )}

            {searchResults.length > 0 && (
              <div className="mt-3 max-h-60 overflow-y-auto rounded-lg border border-dark-700">
                {searchResults.map((media) => (
                  <div
                    key={media.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-dark-800 cursor-pointer"
                    onClick={() => isEdit && addItem(media)}
                  >
                    <span className="text-lg">🎵</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-dark-100">{media.title}</p>
                      <p className="text-xs text-dark-400">{media.artist}</p>
                    </div>
                    {searchLoading && <span className="text-xs text-dark-500">...</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Playlist items with drag-drop */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-dark-100">
                Items ({items.length})
              </h3>
              <span className="text-sm text-dark-400">
                Mode: {MODE_ICONS[formData.mode]} {PLAYLIST_MODES.find((m) => m.value === formData.mode)?.label}
              </span>
            </div>

            {items.length === 0 ? (
              <div className="py-12 text-center text-dark-400">
                No items in this playlist. Search for media to add tracks.
              </div>
            ) : (
              <div className="space-y-1.5">
                {items
                  .slice()
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((item, idx) => (
                    <div
                      key={item.id || item.temp_id}
                      draggable
                      onDragStart={() => handleDragStart(item)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, item)}
                      className="flex items-center gap-3 rounded-lg bg-dark-800/50 px-3 py-2 cursor-move hover:bg-dark-800/70"
                    >
                      <span className="cursor-grab text-dark-500" title="Drag to reorder">
                        ≡
                      </span>
                      <span className="text-xs font-mono text-dark-500 w-6">{idx + 1}</span>
                      <span className="text-xl">🎵</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-dark-100 truncate">
                          {item.title || 'Unknown'}
                        </p>
                        <p className="text-xs text-dark-400 truncate">
                          {item.artist || 'Unknown Artist'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id || item.temp_id)}
                        className="text-xs text-error-400 hover:text-error-300"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
            <Link to="/playlists" className="btn btn-secondary">
              Cancel
            </Link>
            <button type="submit" disabled={saving} className="btn btn-primary">
              {saving ? 'Saving...' : isEdit ? 'Update Playlist' : 'Create Playlist'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
